import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Hand,
  Maximize,
  Pause,
  Play,
  Scan,
  ShieldCheck,
  Target,
  XCircle,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import useStore from '../../store/useStore';
import FlowIcon from '../common/FlowIcon';
import {
  fitTransformToGraph,
  getGraphBounds,
  getNodeBox,
} from '../../utils/graphViewport';
import {
  getBaselineHullBounds,
  getMinimapViewportRect,
  isCompactGraphScale,
  partitionGraphNodes,
} from '../../utils/graphLayout';
import './CanvasRenderer.css';

const MIN_SCALE = 0.06;
const MAX_SCALE = 2.5;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getNodeDimensions(node) {
  return {
    width: node.width || 180,
    height: node.height || 96,
  };
}

function getNodeCenter(node) {
  const { width, height } = getNodeDimensions(node);
  return {
    x: node.x + width / 2,
    y: node.y + height / 2,
  };
}

function getEdgeAnchors(fromNode, toNode) {
  const from = getNodeDimensions(fromNode);
  const to = getNodeDimensions(toNode);
  const fromCenter = getNodeCenter(fromNode);
  const toCenter = getNodeCenter(toNode);
  const dx = toCenter.x - fromCenter.x;
  const dy = toCenter.y - fromCenter.y;

  if (Math.abs(dx) >= Math.abs(dy)) {
    return {
      start: {
        x: fromCenter.x + (dx >= 0 ? from.width / 2 : -from.width / 2),
        y: fromCenter.y,
      },
      end: {
        x: toCenter.x + (dx >= 0 ? -to.width / 2 : to.width / 2),
        y: toCenter.y,
      },
    };
  }

  return {
    start: {
      x: fromCenter.x,
      y: fromCenter.y + (dy >= 0 ? from.height / 2 : -from.height / 2),
    },
    end: {
      x: toCenter.x,
      y: toCenter.y + (dy >= 0 ? -to.height / 2 : to.height / 2),
    },
  };
}

function getEdgePath(fromNode, toNode) {
  const { start, end } = getEdgeAnchors(fromNode, toNode);
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  if (Math.abs(dx) >= Math.abs(dy)) {
    const controlOffset = Math.max(120, Math.abs(dx) * 0.34);
    const direction = dx >= 0 ? 1 : -1;

    return `M ${start.x} ${start.y} C ${start.x + controlOffset * direction} ${start.y}, ${
      end.x - controlOffset * direction
    } ${end.y}, ${end.x} ${end.y}`;
  }

  const controlOffset = Math.max(90, Math.abs(dy) * 0.4);
  const direction = dy >= 0 ? 1 : -1;

  return `M ${start.x} ${start.y} C ${start.x} ${start.y + controlOffset * direction}, ${end.x} ${
    end.y - controlOffset * direction
  }, ${end.x} ${end.y}`;
}

function getLabelPosition(fromNode, toNode) {
  const fromCenter = getNodeCenter(fromNode);
  const toCenter = getNodeCenter(toNode);

  return {
    x: fromCenter.x + (toCenter.x - fromCenter.x) * 0.3,
    y: fromCenter.y + (toCenter.y - fromCenter.y) * 0.3 - 18,
  };
}

function isNodeEmphasized(node, canvasMode) {
  if (canvasMode === 'map') return true;
  if (canvasMode === 'flow') {
    return ['completed', 'active', 'approval', 'queued'].includes(node.state);
  }
  if (canvasMode === 'live') {
    return ['active', 'approval', 'queued', 'paused'].includes(node.state);
  }
  return true;
}

function isEdgeEmphasized(edge, canvasMode) {
  if (canvasMode === 'map') return true;
  if (canvasMode === 'flow') {
    return ['completed', 'active', 'queued', 'rerouted'].includes(edge.state);
  }
  if (canvasMode === 'live') {
    return ['active', 'queued'].includes(edge.state);
  }
  return true;
}

function CanvasRenderer() {
  const containerRef = useRef(null);
  const autoFitRef = useRef(false);
  const {
    apiApproveNode,
    apiPauseSession,
    apiRejectNode,
    apiResumeSession,
    approvalQueue,
    canvasMode,
    edges,
    isExecuting,
    nodes,
    selectedNode,
    setCanvasMode,
    setSelectedNode,
    selectNode,
  } = useStore();
  const [transform, setTransform] = useState({ x: 120, y: 90, scale: 0.56 });
  const [dragState, setDragState] = useState(null);
  const [viewportSize, setViewportSize] = useState({ width: 1200, height: 760 });
  const [toolMode, setToolMode] = useState('select');
  const [hoveredEdge, setHoveredEdge] = useState(null);
  const [spacePressed, setSpacePressed] = useState(false);

  const nodeMap = useMemo(
    () =>
      nodes.reduce((result, node) => {
        result[node.id] = node;
        return result;
      }, {}),
    [nodes]
  );

  const { baseline: baselineNodes, task: taskNodes } = useMemo(
    () => partitionGraphNodes(nodes),
    [nodes]
  );

  const baselineHull = useMemo(
    () => getBaselineHullBounds(baselineNodes),
    [baselineNodes]
  );

  const graphBounds = useMemo(() => getGraphBounds(nodes), [nodes]);

  const minimap = useMemo(
    () => getMinimapViewportRect(transform, viewportSize, graphBounds),
    [transform, viewportSize, graphBounds]
  );

  const compactView = isCompactGraphScale(transform.scale);

  const primaryActionableNode = useMemo(() => {
    if (selectedNode && ['approval', 'active', 'paused', 'queued'].includes(selectedNode.state)) {
      return selectedNode;
    }
    return (
      nodes.find((node) => node.state === 'approval')
      || nodes.find((node) => node.state === 'active')
      || nodes.find((node) => node.state === 'paused')
      || nodes.find((node) => node.state === 'queued')
      || null
    );
  }, [nodes, selectedNode]);

  const showGraphDock = nodes.length > 0 && (isExecuting || approvalQueue.length > 0 || primaryActionableNode);
  const dockFocusNode = primaryActionableNode
    || nodes.find((node) => node.state === 'active')
    || nodes.find((node) => node.state === 'approval')
    || nodes[0]
    || null;

  useEffect(() => {
    if (!primaryActionableNode || selectedNode?.id === primaryActionableNode.id) {
      return;
    }
    if (primaryActionableNode.state === 'approval' && !selectedNode) {
      selectNode(primaryActionableNode.id);
    }
  }, [primaryActionableNode, selectNode, selectedNode]);

  useEffect(() => {
    const updateViewport = () => {
      if (!containerRef.current) return;
      setViewportSize({
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      });
    };

    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  const fitView = useCallback(() => {
    if (!nodes.length) return;
    setTransform(
      fitTransformToGraph(nodes, viewportSize, {
        minScale: MIN_SCALE,
        maxScale: MAX_SCALE,
        padding: 72,
      })
    );
  }, [nodes, viewportSize.height, viewportSize.width]);

  useEffect(() => {
    if (!nodes.length || viewportSize.width === 0 || viewportSize.height === 0) return;
    const bounds = getGraphBounds(nodes);
    const signature = `${nodes.length}:${bounds.width}:${bounds.height}:${nodes.map((n) => `${n.id}@${n.x},${n.y}`).join('|')}`;
    if (autoFitRef.current === signature) return;
    fitView();
    autoFitRef.current = signature;
  }, [fitView, nodes, viewportSize.width, viewportSize.height]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const tagName = document.activeElement?.tagName || '';
      const isTypingTarget = ['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName);
      if (isTypingTarget) return;

      if (event.code === 'Space') {
        event.preventDefault();
        setSpacePressed(true);
      }

      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        fitView();
        return;
      }

      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const key = event.key.toLowerCase();
      if (key === 'm') setCanvasMode('map');
      if (key === 'f') setCanvasMode('flow');
      if (key === 'l') setCanvasMode('live');
      if (key === 'escape') {
        setSelectedNode(null);
        setHoveredEdge(null);
      }
    };

    const handleKeyUp = (event) => {
      if (event.code === 'Space') {
        setSpacePressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [fitView, setCanvasMode, setSelectedNode]);

  const handleWheel = (event) => {
    event.preventDefault();
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();

    if (Math.abs(event.deltaX) > Math.abs(event.deltaY) && Math.abs(event.deltaX) > 2) {
      setTransform((current) => ({
        ...current,
        x: current.x - event.deltaX,
        y: current.y - event.deltaY,
      }));
      return;
    }

    if (Math.abs(event.deltaY) < 1) {
      return;
    }

    const cursorX = event.clientX - rect.left;
    const cursorY = event.clientY - rect.top;
    const zoomFactor = event.deltaY < 0 ? 1.08 : 0.92;
    const nextScale = clamp(transform.scale * zoomFactor, MIN_SCALE, MAX_SCALE);
    const worldX = (cursorX - transform.x) / transform.scale;
    const worldY = (cursorY - transform.y) / transform.scale;

    setTransform({
      scale: nextScale,
      x: cursorX - worldX * nextScale,
      y: cursorY - worldY * nextScale,
    });
  };

  const handleMouseDown = (event) => {
    const panActive = toolMode === 'pan' || spacePressed;
    if (!panActive) return;
    if (event.target.closest('[data-node-interactive="true"]')) return;

    setDragState({
      startX: event.clientX,
      startY: event.clientY,
      originX: transform.x,
      originY: transform.y,
    });
  };

  const handleMouseMove = (event) => {
    if (!dragState) return;

    setTransform((current) => ({
      ...current,
      x: dragState.originX + (event.clientX - dragState.startX),
      y: dragState.originY + (event.clientY - dragState.startY),
    }));
  };

  const handleMouseUp = () => {
    setDragState(null);
  };

  const handleCanvasClick = (event) => {
    if (event.target.closest('[data-node-interactive="true"]')) return;
    if (event.target.closest('[data-edge-interactive="true"]')) return;
    setSelectedNode(null);
  };

  const updateHoveredEdge = (event, edge, fromNode, toNode) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    setHoveredEdge({
      edgeId: edge.id,
      left: event.clientX - rect.left + 18,
      top: event.clientY - rect.top + 18,
      label: `${fromNode.title} → ${toNode.title}`,
      state: formatEdgeState(edge.state),
    });
  };

  const handleFullscreen = async () => {
    if (!containerRef.current) return;

    if (document.fullscreenElement) {
      await document.exitFullscreen();
      return;
    }

    await containerRef.current.requestFullscreen();
  };

  return (
    <div
      ref={containerRef}
      data-tour="graph"
      className={`canvas-renderer canvas-mode-${canvasMode} ${
        toolMode === 'pan' || spacePressed ? 'is-pan-ready' : ''
      } ${dragState ? 'is-panning' : ''}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
      onClick={handleCanvasClick}
    >
      <svg className="graph-svg" viewBox={`0 0 ${viewportSize.width} ${viewportSize.height}`} preserveAspectRatio="none">
        {baselineHull ? (
          <g className="graph-baseline-hull" transform={`translate(${transform.x} ${transform.y}) scale(${transform.scale})`}>
            <rect
              x={baselineHull.minX}
              y={baselineHull.minY}
              width={baselineHull.width}
              height={baselineHull.height}
              rx={18}
            />
            <text
              x={baselineHull.minX + 16}
              y={baselineHull.minY + 22}
              className="graph-baseline-hull-label"
            >
              Syniq baseline ({baselineNodes.length})
            </text>
          </g>
        ) : null}
        <defs>
          <filter id="nodeGlow">
            <feGaussianBlur stdDeviation="9" result="glow" />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <linearGradient id="edgeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(0,212,170,0.35)" />
            <stop offset="100%" stopColor="rgba(121,255,223,0.95)" />
          </linearGradient>

          <marker id="arrow-active" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#00D4AA" />
          </marker>
          <marker id="arrow-muted" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#3f4f60" />
          </marker>
          <marker id="arrow-complete" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#46bda9" />
          </marker>
          <marker id="arrow-rerouted" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#8a7040" />
          </marker>
        </defs>

        <g transform={`translate(${transform.x} ${transform.y}) scale(${transform.scale})`}>
          {edges.map((edge) => {
            const fromNode = nodeMap[edge.from];
            const toNode = nodeMap[edge.to];
            if (!fromNode || !toNode) return null;

            const pathId = `edge-path-${edge.id}`;
            const path = getEdgePath(fromNode, toNode);
            const labelPosition = getLabelPosition(fromNode, toNode);
            const emphasized = isEdgeEmphasized(edge, canvasMode);
            const markerId =
              edge.state === 'active' || edge.state === 'queued'
                ? 'arrow-active'
                : edge.state === 'completed'
                  ? 'arrow-complete'
                  : edge.state === 'rerouted'
                    ? 'arrow-rerouted'
                    : 'arrow-muted';

            return (
              <g
                key={edge.id}
                data-edge-interactive="true"
                className={`graph-edge graph-edge-${edge.state} ${emphasized ? '' : 'is-dim'} ${
                  hoveredEdge?.edgeId === edge.id ? 'is-hovered' : ''
                }`}
                onMouseEnter={(event) => updateHoveredEdge(event, edge, fromNode, toNode)}
                onMouseMove={(event) => updateHoveredEdge(event, edge, fromNode, toNode)}
                onMouseLeave={() => setHoveredEdge(null)}
              >
                <path id={pathId} d={path} fill="none" markerEnd={`url(#${markerId})`} />
                {edge.label ? (
                  <g transform={`translate(${labelPosition.x} ${labelPosition.y})`} className="graph-edge-label">
                    <rect x="-34" y="-13" width="68" height="26" rx="13" />
                    <text textAnchor="middle" dominantBaseline="middle">
                      {edge.label}
                    </text>
                  </g>
                ) : null}
                {isExecuting && edge.state === 'active' ? (
                  <circle className="graph-edge-pulse" r="5">
                    <animateMotion dur="2.6s" repeatCount="indefinite" rotate="auto">
                      <mpath href={`#${pathId}`} />
                    </animateMotion>
                  </circle>
                ) : null}
              </g>
            );
          })}

          {nodes.map((node) => {
            const { width, height } = getNodeDimensions(node);
            const center = getNodeCenter(node);
            const emphasized = isNodeEmphasized(node, canvasMode);
            const isSelected = selectedNode?.id === node.id;

            return (
              <g
                key={node.id}
                data-node-interactive="true"
                className={`graph-node graph-node-${node.state} graph-node-${node.shape} ${
                  emphasized ? '' : 'is-dim'
                } ${isSelected ? 'is-selected' : ''} ${compactView || node.config?.compact ? 'is-compact' : ''} ${
                  node.config?.group === 'baseline' ? 'is-baseline' : ''
                }`}
                onClick={() => selectNode(node.id)}
              >
                {node.shape === 'diamond' ? (
                  <polygon
                    points={`${center.x},${node.y} ${node.x + width},${center.y} ${center.x},${node.y + height} ${node.x},${center.y}`}
                    filter={node.state === 'active' || node.state === 'approval' ? 'url(#nodeGlow)' : undefined}
                  />
                ) : (
                  <rect
                    x={node.x}
                    y={node.y}
                    width={width}
                    height={height}
                    rx="24"
                    filter={node.state === 'active' || node.state === 'approval' ? 'url(#nodeGlow)' : undefined}
                  />
                )}

                <g transform={`translate(${node.x + 18} ${node.y + 16})`}>
                  <rect className="graph-node-icon-backdrop" width="28" height="28" rx="10" />
                  <g transform="translate(5 5)">
                    <FlowIcon name={node.icon} size={18} />
                  </g>
                </g>

                <text className="graph-node-title" x={node.x + 56} y={node.y + 34}>
                  {node.title}
                </text>
                {!compactView && !node.config?.compact ? (
                  <text className="graph-node-subtitle" x={node.x + 56} y={node.y + 58}>
                    {node.subtitle}
                  </text>
                ) : null}
                {!compactView && !node.config?.compact ? (
                  <text className="graph-node-caption" x={node.x + 18} y={node.y + height - 14}>
                    {formatNodeCaption(node)}
                  </text>
                ) : null}

                {node.state === 'approval' ? (
                  <circle className="graph-node-badge" cx={node.x + width - 18} cy={node.y + 18} r="8" />
                ) : null}

                {primaryActionableNode?.id === node.id && node.state === 'approval' ? (
                  <foreignObject
                    x={node.x + width / 2 - 108}
                    y={node.y + height + 10}
                    width="216"
                    height="40"
                  >
                    <div className="graph-node-action-chip" xmlns="http://www.w3.org/1999/xhtml">
                      <button
                        type="button"
                        className="graph-node-action-btn graph-node-action-btn--approve"
                        onClick={(event) => {
                          event.stopPropagation();
                          apiApproveNode(node.id);
                        }}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="graph-node-action-btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          apiRejectNode(node.id);
                        }}
                      >
                        Reject
                      </button>
                    </div>
                  </foreignObject>
                ) : null}
              </g>
            );
          })}
        </g>
      </svg>

      {showGraphDock && dockFocusNode ? (
        <div className="graph-orchestration-dock" data-tour="graph-controls">
          <div className="graph-orchestration-dock-copy">
            <strong>{dockFocusNode.title || 'Orchestration step'}</strong>
            <span>{formatNodeCaption(dockFocusNode)}</span>
          </div>
          <div className="graph-orchestration-dock-actions">
            {dockFocusNode.state === 'approval' ? (
              <>
                <button type="button" className="btn btn-primary" onClick={() => apiApproveNode(dockFocusNode.id)}>
                  <ShieldCheck size={14} />
                  Approve
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => apiRejectNode(dockFocusNode.id)}>
                  <XCircle size={14} />
                  Reject
                </button>
              </>
            ) : null}
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => (isExecuting ? apiPauseSession() : apiResumeSession())}
            >
              {isExecuting ? <Pause size={14} /> : <Play size={14} />}
              {isExecuting ? 'Pause run' : 'Resume run'}
            </button>
          </div>
          {approvalQueue.length > 0 ? (
            <span className="graph-orchestration-dock-meta">{approvalQueue.length} approval{approvalQueue.length === 1 ? '' : 's'} queued</span>
          ) : (
            <span className="graph-orchestration-dock-meta">Controls stay on the graph — no need to open the side panel first</span>
          )}
        </div>
      ) : null}

      {minimap && nodes.length > 4 ? (
        <div className="graph-minimap" aria-hidden="true">
          <svg width={minimap.mapW} height={minimap.mapH} viewBox={`0 0 ${minimap.mapW} ${minimap.mapH}`}>
            <rect
              className="graph-minimap-bounds"
              x={0}
              y={0}
              width={minimap.bounds.width * minimap.scale}
              height={minimap.bounds.height * minimap.scale}
            />
            {nodes.map((node) => {
              const box = getNodeBox(node);
              return (
                <rect
                  key={`mini-${node.id}`}
                  className={`graph-minimap-node ${node.config?.group === 'baseline' ? 'is-baseline' : ''}`}
                  x={(box.left - minimap.bounds.minX) * minimap.scale}
                  y={(box.top - minimap.bounds.minY) * minimap.scale}
                  width={Math.max(2, box.width * minimap.scale * 0.35)}
                  height={Math.max(2, box.height * minimap.scale * 0.35)}
                />
              );
            })}
            <rect
              className="graph-minimap-viewport"
              x={minimap.viewX}
              y={minimap.viewY}
              width={minimap.viewW}
              height={minimap.viewH}
            />
          </svg>
        </div>
      ) : null}

      <div className="canvas-zoom-controls" data-tour="graph-zoom">
        <button className="canvas-control-button" onClick={() => setTransform((current) => ({ ...current, scale: clamp(current.scale * 1.12, MIN_SCALE, MAX_SCALE) }))}>
          <ZoomIn size={16} />
        </button>
        <button className="canvas-control-button" onClick={() => setTransform((current) => ({ ...current, scale: clamp(current.scale * 0.9, MIN_SCALE, MAX_SCALE) }))}>
          <ZoomOut size={16} />
        </button>
        <button className="canvas-control-button" onClick={fitView}>
          <Scan size={16} />
        </button>
      </div>

      <div className="canvas-bottom-toolbar" data-tour="graph-toolbar">
        <button
          type="button"
          title="Select nodes"
          aria-label="Select nodes"
          className={`canvas-toolbar-button ${toolMode === 'select' ? 'is-active' : ''}`}
          onClick={() => setToolMode('select')}
        >
          <Target size={16} />
        </button>
        <button
          type="button"
          title="Pan canvas (or hold Space)"
          aria-label="Pan canvas"
          className={`canvas-toolbar-button ${toolMode === 'pan' ? 'is-active' : ''}`}
          onClick={() => setToolMode('pan')}
        >
          <Hand size={16} />
        </button>
        <button type="button" title="Fit graph to view" aria-label="Fit graph to view" className="canvas-toolbar-button" onClick={fitView}>
          <Scan size={16} />
        </button>
        <button type="button" title="Fullscreen canvas" aria-label="Fullscreen canvas" className="canvas-toolbar-button" onClick={handleFullscreen}>
          <Maximize size={16} />
        </button>
      </div>

      {hoveredEdge ? (
        <div className="canvas-edge-tooltip" style={{ left: hoveredEdge.left, top: hoveredEdge.top }}>
          <span>{hoveredEdge.label}</span>
          <strong>{hoveredEdge.state}</strong>
        </div>
      ) : null}
    </div>
  );
}

function formatNodeCaption(node) {
  if (node.state === 'approval') return 'Operator checkpoint';
  if (node.confidence) return `${node.confidence}% confidence`;
  return node.type;
}

function formatEdgeState(state) {
  switch (state) {
    case 'active':
      return 'Executing path';
    case 'queued':
      return 'Queued branch';
    case 'completed':
      return 'Resolved path';
    case 'rerouted':
      return 'Rerouted path';
    default:
      return 'Inactive path';
  }
}

export default CanvasRenderer;
